import React, {
  useState,
  useCallback,
  useContext,
  useMemo,
} from 'react';
import { compose } from 'recompose';
import { observer } from 'mobx-react';
import format from 'string-template';
import PropTypes from 'prop-types';
import { Tooltip, IconButton } from '@mui/material';
import { GridRow } from '@mui/x-data-grid';
import { Draggable, Container as DraggableContainer } from 'react-smooth-dnd';

import { ReactComponent as CreateIcon } from '../../../../../assets/icons/v2/creation-plus-circle.svg';
import { ReactComponent as DragIcon } from '../../../../../assets/icons/v2/drag-alt.svg';
import { ReactComponent as VideoIcon } from '../../../../../assets/icons/video-logo.svg';
import useQuickSearch from '../../../../../hooks/useQuickSearch';
import useComponentMounted from '../../../../../hooks/useComponentMounted';
import useSessionStore from '../../../../../hooks/useSessionStore';
import {
  HeaderRow,
  TitleContainer,
  Title,
} from '../../../../../components/v2/Header';
import {
  PrimaryButton,
} from '../../../../../components/Button/ActionButtons';
import { CoachingActivity } from '../../../../../utils/log';
import useLogger from '../../../../../hooks/useLogger';
import Exercise from '../../../../Model/Exercise';
import {
  TagCategory,
} from '../../../../Model/ExerciseTag';
import useToast from '../../../../hooks/useToast';
import ExerciseContext, { withExerciseContextReady } from '../../../../context/ExerciseContext';
import ExerciseTagContext from '../../../../context/ExerciseTagContext';
import QuickSearchToolbar from '../../../../components/QuickSearchToolbar';
import LabelCheckbox from '../../../../components/LabelCheckbox';
import GenericDataGrid from '../../../../components/GenericDataGrid';
import LoadingOverlay from '../../../../components/LoadingOverlay';
import ConfirmDialog from '../../../../components/ConfirmDialog';
import ExerciseVideoIssueChip from '../../../../components/ExerciseVideoIssueChip';
import {
  ActionType,
  canPerformAction,
  isModalViewAction,
} from '../utils';

import './ExerciseTable.css';
import getColumns from './columns';
import ActionButtons from './ActionButtons';
import {
  TagList,
  ExerciseTag,
  ExerciseName,
  ExerciseNameText,
  Thumbnail,
  StyledCollapsibleSelect,
  HeaderActions,
} from './styles';
import texts from './texts';

const TAGS_PER_ROW = 2;
const EQUIPMENTS_PER_ROW = 1;

const ExercisesTable = ({
  selectedExercise,
  actionPerformed,
  handleActionClick,
  isWorkoutDesignerView,
  isExerciseChangeView,
}) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedTags, setSelectedTags] = useState([]);

  const {
    exercises,
    activeExercises,
    showArchivedExercises,
    OnChangeCheckBox,
    isLoading,
  } = useContext(ExerciseContext);
  const {
    tags: exerciseTags,
  } = useContext(ExerciseTagContext);

  const isComponentMountedRef = useComponentMounted();
  const { isAdmin } = useSessionStore();

  const { showToast } = useToast();
  const { logCoachingActivity } = useLogger();

  // TODO: Move this memoized list to the Exercise Context when we need to reuse the collapsible select filter
  // Categorize the tag options under the defined tag categories
  const categorizedTagOptions = useMemo(() => {
    const categories = Object.keys(TagCategory).reduce((acc, category) => {
      acc[category] = [];
      return acc;
    }, {});

    exerciseTags.forEach((tag) => {
      const {
        id,
        tag: tagName,
        category,
      } = tag;
      if (categories[category]) {
        categories[category].push({ id, label: tagName });
      }
    });

    // Transform categories object into array of objects with category and items
    return Object.keys(categories).map((category) => ({
      category: texts.tagCategoryOption[category || ''],
      items: categories[category],
    }));
  }, [
    exerciseTags,
  ]);

  const handleArchiveClick = useCallback(async () => {
    setIsProcessing(true);
    await selectedExercise.updateArchiveStatus(!selectedExercise.current);
    logCoachingActivity(selectedExercise.current ? CoachingActivity.ARCHIVED_EXERCISE
      : CoachingActivity.UNARCHIVED_EXERCISE, { exerciseId: selectedExercise.id });
    if (isComponentMountedRef.current) {
      setIsProcessing(false);
      showToast(format(texts.successfullyProcessed, {
        action: texts.action[actionPerformed],
      }));
      handleActionClick(null, null);
    }
  }, [
    showToast,
    isComponentMountedRef,
    actionPerformed,
    selectedExercise,
    handleActionClick,
    logCoachingActivity,
  ]);

  const renderDragHandle = useCallback(({ index }) => (
    <IconButton
      key={index}
      className="drag-handle"
    >
      <DragIcon />
    </IconButton>
  ), []);

  const renderExerciseName = useCallback(({
    row: {
      name,
      videoPreviewThumbnail,
      videoUrl,
      hasPendingVideoProcessing,
    } = {},
  }) => (
    <ExerciseName>
      {(isWorkoutDesignerView || isExerciseChangeView) && videoPreviewThumbnail && (
        <Thumbnail src={videoPreviewThumbnail} alt={name} />
      )}
      <Tooltip title={name} arrow>
        <ExerciseNameText>
          {name}
        </ExerciseNameText>
      </Tooltip>
      {(isWorkoutDesignerView || isExerciseChangeView) && (
        <ExerciseVideoIssueChip videoUrl={videoUrl} isVideoProcessing={hasPendingVideoProcessing} iconOnly />
      )}
    </ExerciseName>

  ), [
    isWorkoutDesignerView,
    isExerciseChangeView,
  ]);

  // Render the tags cell of each row.
  const renderTags = useCallback(({ row: { tags } = {} }) => {
    let tagsCell = texts.emptyText;

    // If we have tags, then we render them with different styles.
    if (tags.length > 0) {
      const initialTags = tags.slice(0, TAGS_PER_ROW);
      const remainingTags = tags.slice(TAGS_PER_ROW, tags.length);
      const tooltipText = remainingTags.map(({ tag }) => tag).join(', ');

      tagsCell = (
        <TagList>
          {initialTags.map(({ id, tag }) => (
            <ExerciseTag key={id}>{tag}</ExerciseTag>
          ))}
          {!!remainingTags.length && (
            <Tooltip title={tooltipText} placement="top" arrow>
              <ExerciseTag>{format(texts.moreTags, { amount: remainingTags.length })}</ExerciseTag>
            </Tooltip>
          )}
        </TagList>
      );
    }

    return tagsCell;
  }, []);

  const renderEquipments = useCallback(({ row: { tags } = {} }) => {
    let equipmentsCell = texts.emptyText;

    if (tags.length > 0) {
      const equipments = tags.filter(({ category }) => category === TagCategory.EQUIPMENT);
      if (equipments.length === 0) return equipmentsCell;

      const initialEquipment = equipments.slice(0, EQUIPMENTS_PER_ROW);
      const remainingEquipments = equipments.length > 1 ? equipments.slice(EQUIPMENTS_PER_ROW, equipments.length) : [];
      const tooltipText = remainingEquipments.map(({ tag }) => tag).join(', ');

      equipmentsCell = (
        <TagList>
          {initialEquipment.map(({ id, tag }) => (
            <ExerciseTag key={id}>{tag}</ExerciseTag>
          ))}
          {!!remainingEquipments.length && (
            <Tooltip title={tooltipText} placement="top" arrow>
              <ExerciseTag>{format(texts.moreTags, { amount: remainingEquipments.length })}</ExerciseTag>
            </Tooltip>
          )}
        </TagList>
      );
    }

    return equipmentsCell;
  }, []);

  const renderActions = useCallback(({ row }) => (
    <ActionButtons
      row={row}
      handleActionClick={handleActionClick}
      isWorkoutDesignerView={isWorkoutDesignerView}
      isExerciseChangeView={isExerciseChangeView}
    />
  ), [
    handleActionClick,
    isWorkoutDesignerView,
    isExerciseChangeView,
  ]);

  const columns = getColumns({
    dragHandleRenderCell: renderDragHandle,
    exerciseNameRenderCell: renderExerciseName,
    tagsRenderCell: renderTags,
    equipmentsRenderCell: renderEquipments,
    actionsRenderCell: renderActions,
    isWorkoutDesignerView,
    isExerciseChangeView,
    isAdmin,
  });

  // Get filtered rows for toolbar search value
  const {
    filteredRows: quickSearchRows,
    toolbarProps,
  } = useQuickSearch(isWorkoutDesignerView ? activeExercises : exercises, columns);

  /*
   * If tags are selected, rows containing at least one of the selected tags are included in the filtered result,
   * otherwise all rows are included.
   */
  const filteredRows = useMemo(() => (
    selectedTags.length > 0
      ? quickSearchRows.filter((row) => (
        selectedTags.some((tag) => row.tags.some((exerciseTag) => exerciseTag.id === tag))
      ))
      : quickSearchRows
  ), [
    quickSearchRows,
    selectedTags,
  ]);

  const filterTools = useMemo(() => {
    const filters = [];
    if (!isWorkoutDesignerView && !isExerciseChangeView) {
      filters.push({
        Component: LabelCheckbox,
        id: 'archived-exercises-filter',
        props: {
          isChecked: showArchivedExercises,
          description: texts.archivedCheckbox,
          onChange: (isChecked) => OnChangeCheckBox(isChecked),
        },
      });
    }
    filters.push({
      Component: StyledCollapsibleSelect,
      id: 'tags-filter',
      props: {
        options: categorizedTagOptions,
        onChange: (options) => setSelectedTags(options),
        selectLabel: 'Filter Exercises',
        showResetOption: true,
        $isWorkoutDesignerView: isWorkoutDesignerView,
      },
    });
    return filters;
  }, [
    categorizedTagOptions,
    isWorkoutDesignerView,
    showArchivedExercises,
    isExerciseChangeView,
    OnChangeCheckBox,
  ]);

  const DraggableRow = (rowData) => {
    const { row, rowId } = rowData;
    return (
      <DraggableContainer
        dragHandleSelector=".drag-handle"
        groupName="activities"
        dragClass="dragged-exercise-row"
        getGhostParent={() => document.body}
        getChildPayload={() => ({ isFromExerciseTable: true, exercise: row })}
        behaviour="copy"
      >
        <Draggable key={rowId}>
          <GridRow key={rowId} {...rowData} />
        </Draggable>
      </DraggableContainer>
    );
  };

  return (
    <>
      {!isWorkoutDesignerView && !isExerciseChangeView && (
        <HeaderRow>
          <TitleContainer>
            <Title>{texts.title}</Title>
          </TitleContainer>
          <HeaderActions>
            <PrimaryButton
              onClick={() => handleActionClick(null, ActionType.BULK_CREATE)}
              icon={<VideoIcon />}
              variant="light"
            >
              {texts.createFromVideos}
            </PrimaryButton>
            <PrimaryButton
              onClick={() => handleActionClick(null, ActionType.CREATE)}
              icon={<CreateIcon />}
            >
              {texts.newExercise}
            </PrimaryButton>
          </HeaderActions>
        </HeaderRow>
      )}
      <GenericDataGrid
        rows={filteredRows}
        columns={columns}
        // If the rows are rendered with the 'DraggableRow' we get the id as 'rowId'
        getRowId={(row) => row.rowId || row.id}
        components={{
          Toolbar: QuickSearchToolbar,
          ...(isWorkoutDesignerView && { Row: DraggableRow }),
        }}
        componentsProps={{
          toolbar: {
            ...toolbarProps,
            placeholder: texts.searchPlaceholder,
            filterTools,
          },
        }}
      />
      {canPerformAction(actionPerformed, selectedExercise) && (
        <ConfirmDialog
          isOpen={isModalViewAction(actionPerformed)}
          onConfirm={handleArchiveClick}
          onCancel={() => handleActionClick(null, null)}
          dialogTexts={{
            title: format(texts.actionText, {
              action: texts.action[actionPerformed],
              exercise: selectedExercise.name,
            }),
          }}
        />
      )}
      <LoadingOverlay isLoading={isProcessing || isLoading} />
    </>
  );
};

ExercisesTable.propTypes = {
  selectedExercise: PropTypes.instanceOf(Exercise),
  actionPerformed: PropTypes.string,
  handleActionClick: PropTypes.func,
  isWorkoutDesignerView: PropTypes.bool,
  isExerciseChangeView: PropTypes.bool,
};

ExercisesTable.defaultProps = {
  selectedExercise: null,
  actionPerformed: null,
  handleActionClick: () => { },
  isWorkoutDesignerView: false,
  isExerciseChangeView: false,
};

export default compose(
  withExerciseContextReady,
  observer,
)(ExercisesTable);
